import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { formatDisplayDateTime } from "utils/dateFormat";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { formatMoney } from "utils/utils";
import { getInvoiceNumber } from "utils/invoiceDashboardUtils";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import InvoiceTemplate from "./InvoiceTemplate";
import useInvoiceActivationDecision from "./useInvoiceActivationDecision";

const InvoiceActivationRequests = () => {
  // Invoices already activated or rejected in this session. Their row keeps its stale pending copy
  // until the refetch lands, and until then the buttons would act on a decision already made.
  const [settledInvoiceIds, setSettledInvoiceIds] = useState(() => new Set());
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState(null);
  const previewRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // The endpoint pages its response — the queue grows with every draft sent for review.
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["invoice-activation-pending", page, pageSize],
    queryFn: () =>
      axios
        .get(`${API_URL}/invoice-verification/activations/pending`, {
          params: { page, size: pageSize },
        })
        .then((r) => r.data?.data),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const requests = data?.content ?? [];
  const totalItems = data?.totalItems ?? 0;

  // Approving the last row on a page leaves it empty — fall back rather than show nothing.
  useEffect(() => {
    if (!isFetching && page > 0 && requests.length === 0) setPage((prev) => prev - 1);
  }, [isFetching, page, requests.length]);

  const { openApprove, openReject, dialogs } = useInvoiceActivationDecision({
    onSettled: (invoiceId) => {
      setSettledInvoiceIds((ids) => new Set(ids).add(invoiceId));
      return refetch();
    },
  });

  const printPreview = useReactToPrint({
    contentRef: previewRef,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
    onAfterPrint: () => setPreviewInvoice(null),
  });

  useEffect(() => {
    if (previewInvoice) printPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewInvoice]);

  // The queue row only carries a summary, so the full invoice is fetched on demand.
  const handlePreview = async (invoiceId) => {
    setLoadingPreviewId(invoiceId);
    try {
      const response = await axios.get(`${API_URL}/invoice/${invoiceId}`);
      setPreviewInvoice(response.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load invoice preview");
    } finally {
      setLoadingPreviewId(null);
    }
  };

  return (
    <Box>
      <TableContainer className="border border-app-border rounded-lg">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Invoice</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Sent by</TableCell>
              <TableCell>Sent at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((item) => (
              <TableRow key={item.invoiceId}>
                <TableCell>{item.clientName || "-"}</TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="body2">{item.invoiceTitle || "-"}</Typography>
                    {item.invoiceId && (
                      <Typography variant="caption" className="text-app-text-muted">
                        {getInvoiceNumber(item)}
                        {item.invoiceTotal != null && ` · ${formatMoney(item.invoiceTotal)}`}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatMoney(item.invoiceTotal || 0)}</TableCell>
                <TableCell>{item.requestedByName || "-"}</TableCell>
                <TableCell>{formatDisplayDateTime(item.requestedAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    <Tooltip title="Preview invoice">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Preview invoice"
                          disabled={loadingPreviewId != null}
                          onClick={() => handlePreview(item.invoiceId)}
                        >
                          {loadingPreviewId === item.invoiceId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={settledInvoiceIds.has(item.invoiceId)}
                      onClick={() => openApprove(item)}
                    >
                      Activate
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={settledInvoiceIds.has(item.invoiceId)}
                      onClick={() => openReject(item)}
                    >
                      Reject
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!isFetching && requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" className="text-app-text-muted">
                  No invoices pending activation.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      {dialogs}

      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        {previewInvoice && <InvoiceTemplate printData={previewInvoice} contentRef={previewRef} />}
      </div>
    </Box>
  );
};

export default InvoiceActivationRequests;
